import { DataCardinalities, DataTypes } from "../../../data/DataManager";
import { GridFilterTypes } from "../../../grid/AgGridManager";
import { EMPTY_VALUE, FieldSchema, FieldValueArray, FieldValueObject, FieldValues } from "../../../Schema";


export interface QualificationDocumentValue extends FieldValueObject {
  name: string;
  documentId: string;
}

export interface QualificationDocumentsValue extends FieldValueObject {
  isSynchronized: boolean;
  value: QualificationDocumentValue[];
}

export const qualificationDocuments: FieldSchema = {
  grid: {
    displayed: true,
    order: 30,
    filter: GridFilterTypes.NUMBER,
    integerOnly: true,
  },
  name: "qualificationDocuments",
  // `isSynchronized` is selected even though the field's own value is just the document
  // list. It is part of the stored attribute, and the review flow diffs a fetched record
  // against a proposed change — leaving it out of the fetched side made it read as newly
  // created on every review of an equipment that has documents.
  graphql: { selection: "qualificationDocuments { isSynchronized value { documentId name } }" },
  dataTransform: {
    fromGraphql: {
      getter: ({ qualificationDocuments }) => {
        const value = (qualificationDocuments as QualificationDocumentsValue) || EMPTY_VALUE;
        if (value) {
          if (value.value) {
            return value.value.map((document: QualificationDocumentValue) => ({
              name: document.name,
              documentId: document.documentId,
            })) ?? EMPTY_VALUE;
          }
          if (Array.isArray(value)) {
            return (value as { name: string; documentId: string }[]).map((document) => ({
              name: document.name,
              documentId: document.documentId,
            })) ?? EMPTY_VALUE;
          }
        }
        return EMPTY_VALUE;
      },
    },
    toGraphql: {
      getter: ({ qualificationDocuments }) => {
        if (qualificationDocuments && Array.isArray(qualificationDocuments) && (qualificationDocuments as FieldValueArray).length) {
          return { value: qualificationDocuments };
        }
        return EMPTY_VALUE;
      },
    },
    // Stored wrapped, exactly as GraphQL takes it — writing the bare list would replace the
    // whole attribute with something shaped differently from every stored record.
    //
    // `isSynchronized` sits alongside `value` and is owned by the sync, not by an edit, so
    // it is carried across from whatever the record already had rather than rebuilt. The
    // field's own value is only the document list, which is why it has to be read from
    // `originalValues` here instead of from the value being written.
    toDb: {
      getter: ({ qualificationDocuments, originalValues }) => {
        if (qualificationDocuments && Array.isArray(qualificationDocuments) && (qualificationDocuments as FieldValueArray).length) {
          const stored = (originalValues as FieldValues | undefined)?.["qualificationDocuments"];
          const isSynchronized = (stored as QualificationDocumentsValue | undefined)?.isSynchronized;
          return isSynchronized === undefined ?
            { value: qualificationDocuments } :
            { value: qualificationDocuments, isSynchronized };
        }
        return EMPTY_VALUE;
      },
      fields: ["qualificationDocuments", "originalValues"],
    },
  },
  data: {
    type: DataTypes.OBJECT,
    cardinality: DataCardinalities.MULTIPLE,
  },
  display: {
    displayed: true,
  },
  form: { label: "Qualification documents" },
};
